import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { titleCase } from '../../lib/format'
import { Alert, Button, Card, CardBody, Input, Select, Textarea } from '../../components/ui'

/* Faithful port of anansi/app/templates/admin/role_form.html + its views
 * admin/routes.py:create_role()/edit_role(). Shared component for both -
 * `mode` (from the route) picks create vs edit, same as the Jinja
 * template's own `mode` variable. */

interface Capability { key: string; group: string; label: string }
interface MetaResponse { ok: boolean; tiers: string[]; scopes: string[]; capabilities: Capability[] }
interface BuilderRole {
  key: string; title: string; description?: string; tier: string; scope: string
  level: number; home?: string; caps: string[]; system: boolean; active: boolean
}
interface RoleResponse { ok: boolean; item: BuilderRole }

function slugify(raw: string): string {
  return raw.trim().toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '')
}

export default function AdminRoleForm({ mode }: { mode: 'create' | 'edit' }) {
  const { key } = useParams<{ key: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { data: meta } = useQuery({
    queryKey: ['admin', 'roles', 'meta'],
    queryFn: () => api.get<MetaResponse>('/admin/roles/meta'),
  })
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'roles', key],
    queryFn: () => api.get<RoleResponse>(`/admin/roles/${key}`),
    enabled: mode === 'edit' && Boolean(key),
  })

  const [titleVal, setTitleVal] = useState('')
  const [selectedCaps, setSelectedCaps] = useState<string[] | null>(null)

  const role = mode === 'edit' ? data?.item : undefined
  const caps = selectedCaps ?? role?.caps ?? []

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<RoleResponse>('/admin/roles', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'roles'] })
      navigate('/roles')
    },
  })
  const update = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch<RoleResponse>(`/admin/roles/${key}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'roles'] })
      navigate('/roles')
    },
  })
  const saving = create.isPending || update.isPending
  const saveError = create.error ?? update.error

  if (mode === 'edit') {
    if (!key) return <Navigate to="/roles" replace />
    if (isLoading || !role) return <Card><CardBody>Loading…</CardBody></Card>
    if (role.system) {
      return (
        <Alert tone="danger">
          Core roles cannot be edited.
          <div style={{ marginTop: 'var(--sp-3)' }}><Link to="/roles">Back to roles</Link></div>
        </Alert>
      )
    }
  }

  const groups = Array.from(new Set((meta?.capabilities ?? []).map((c) => c.group)))

  function toggleCap(capKey: string, checked: boolean) {
    const current = caps
    setSelectedCaps(checked ? [...current, capKey] : current.filter((c) => c !== capKey))
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const body: Record<string, unknown> = {
      title: String(form.get('title') ?? '').trim(),
      description: String(form.get('description') ?? '').trim(),
      tier: String(form.get('tier') ?? ''),
      scope: String(form.get('scope') ?? ''),
      level: Number(form.get('level') ?? 20),
      home: String(form.get('home') ?? '').trim() || 'reports.index',
      caps,
    }
    if (mode === 'create') {
      body.key = String(form.get('key') ?? '').trim() || slugify(titleVal)
      create.mutate(body)
    } else {
      body.active = form.get('active') === 'on'
      update.mutate(body)
    }
  }

  return (
    <>
      <div style={{ marginBottom: 'var(--sp-4)' }}>
        <Link to="/roles">← Back to roles</Link>
      </div>
      <div className="page-head">
        <div>
          <h1>{mode === 'create' ? 'New Role' : `Edit ${role?.title}`}</h1>
        </div>
      </div>

      {saveError && (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          <Alert tone="danger">{saveError instanceof ApiError ? saveError.message : 'Could not save this role.'}</Alert>
        </div>
      )}

      <Card>
        <CardBody>
          <form onSubmit={onSubmit} className="stack">
            <Input
              label="Title" name="title" required
              defaultValue={role?.title}
              onChange={(e) => setTitleVal(e.target.value)}
            />
            {mode === 'create' ? (
              <Input label="Key" name="key" placeholder="auto from title if left blank" />
            ) : (
              <Input label="Key" value={role?.key} disabled />
            )}
            <Textarea label="Description" name="description" rows={2} defaultValue={role?.description} />

            <div className="grid grid--2">
              <Select label="Tier" name="tier" defaultValue={role?.tier ?? 'mining_ops'}>
                {(meta?.tiers ?? []).map((t) => <option key={t} value={t}>{titleCase(t)}</option>)}
              </Select>
              <Select label="Data scope" name="scope" defaultValue={role?.scope ?? 'org'}>
                {(meta?.scopes ?? []).map((s) => <option key={s} value={s}>{titleCase(s)}</option>)}
              </Select>
            </div>
            <div className="grid grid--2">
              <Input label="Level" name="level" type="number" min={0} max={99} defaultValue={role?.level ?? 20} />
              <Input label="Home" name="home" defaultValue={role?.home || 'reports.index'} placeholder="reports.index" />
            </div>

            <div>
              <p style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: 'var(--sp-2)' }}>Capabilities</p>
              <div className="stack" style={{ gap: 'var(--sp-4)' }}>
                {groups.map((g) => (
                  <div key={g}>
                    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 6 }}>{g}</p>
                    <div className="grid grid--2">
                      {(meta?.capabilities ?? []).filter((c) => c.group === g).map((c) => (
                        <label key={c.key} className="row" style={{ gap: 6 }}>
                          <input
                            type="checkbox" checked={caps.includes(c.key)}
                            onChange={(e) => toggleCap(c.key, e.target.checked)}
                          />
                          {c.label}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {mode === 'edit' && (
              <label className="row" style={{ gap: 6 }}>
                <input type="checkbox" name="active" defaultChecked={role?.active} /> Active
              </label>
            )}

            <div className="row" style={{ gap: 'var(--sp-2)' }}>
              <Button type="submit" loading={saving} confirm={{ title: "Save these changes?" }}>Save</Button>
              <Link to="/roles" className="btn btn--ghost">Cancel</Link>
            </div>
          </form>
        </CardBody>
      </Card>
    </>
  )
}
